// StudentDashboard.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { BookOpenCheck, BrainCircuit, ChartNoAxesCombined, Trophy, X } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import axios from "../../api/axios";
import {
  masterContainer,
  masterAmbientGradient1,
  masterAmbientGradient2,
  masterScrollArea,
  masterPanelCard,
  masterGlowGradientsTop,
  masterGlowGradientsBottom,
  masterBadgeControl,
  masterHeadingMain,
  masterStickyHeadRow,
  masterTableRow,
  masterEmptyState,
  masterCardBox,
  masterCardIconBox,
  masterCardTextBox,
  masterCardTitle,
  masterCardLabel,
  masterWelcomeTitle,
  masterWelcomeSubtitle,
  masterTableCodeCell,
  masterTableNameCell,
  masterTableBadgeActive,
  masterTableDateCell,
} from "../../GlobalComponents/MasterCssClass";

const formatScore = (value) => Number(value || 0).toFixed(3);

const formatDuration = (seconds) => {
  const sec = Number(seconds || 0);
  if (sec <= 0) return "0 sec";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m === 0) return `${s} sec`;
  if (s === 0) return `${m} min`;
  return `${m} min ${s} sec`;
};

const getOrdinalSuffix = (num) => {
  const n = Number(num || 1);
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Custom DD-MM-YYYY Date Formatter with 12-hour time
const formatDate = (dateString) => {
  if (!dateString) return "---";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "---";

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${day}-${month}-${year}, ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
};

const StudentDashboard = () => {
  const { user } = useContext(AuthContext);
  const [historyRows, setHistoryRows] = useState([]);
  const [tenantOrgName, setTenantOrgName] = useState(user?.orgName || user?.tenantName || "");
  const [tenantLogo, setTenantLogo] = useState("");
  const [tenantExams, setTenantExams] = useState([]);

  // --- MODAL & EXAM MANAGEMENT STATES ---
  const [availableExams, setAvailableExams] = useState([]);
  const [selectedNewExam, setSelectedNewExam] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingLoading, setAddingLoading] = useState(false);

  const loadDashboardData = async () => {
    try {
      const res = await axios.get("/master/student-dashboard/dashboard-data");
      
      if (res.data && res.data.organizationName) {
        setTenantOrgName(res.data.organizationName);
      }

      if (res.data && res.data.organizationDetails?.logo) {
        setTenantLogo(res.data.organizationDetails.logo);
      }

      if (res.data && Array.isArray(res.data.exams)) {
        setTenantExams(res.data.exams);
      }

      const attemptsData = res.data?.attempts || res.data?.data?.attempts || res.data?.data;
      if (Array.isArray(attemptsData)) {
        // Sort descending so latest data appears at the top
        const sortedAttempts = [...attemptsData].sort(
          (a, b) => new Date(b?.submittedAt || 0).getTime() - new Date(a?.submittedAt || 0).getTime()
        );
        setHistoryRows(sortedAttempts);
      } else {
        setHistoryRows([]);
      }
    } catch (err) {
      console.error("Dashboard data fetch error:", err);
      setHistoryRows([]);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const fetchAvailableExams = async () => {
    try {
      const res = await axios.get("/master/student-dashboard/available-exams");
      setAvailableExams(Array.isArray(res.data?.data) ? res.data.data : []);
      setShowAddModal(true);
    } catch (err) {
      console.error("Failed to load available exams", err);
    }
  };

  const handleAddExamSubmit = async () => {
    if (!selectedNewExam) return;
    setAddingLoading(true);
    try {
      await axios.post("/master/student-dashboard/add-exam", { examCode: selectedNewExam });
      setShowAddModal(false);
      setSelectedNewExam("");
      await loadDashboardData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to add exam");
    } finally {
      setAddingLoading(false);
    }
  };

  const handleRemoveExam = async (examCode) => {
    const ok = window.confirm("Are you sure you want to remove this exam?");
    if (!ok) return;

    try {
      await axios.post("/master/student-dashboard/remove-exam", { examCode });
      await loadDashboardData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to remove exam");
    }
  };

  const selectedExamObjects = useMemo(
    () => (Array.isArray(tenantExams) ? tenantExams : []),
    [tenantExams]
  );

  const latestAttempt = historyRows[0] || null;
  const totalAttempts = historyRows.length;

  return (
    <div className={masterContainer}>
      <div className={masterAmbientGradient1} />
      <div className={masterAmbientGradient2} />
      
      <div className={masterScrollArea}>
        
        {/* Top Welcome Panel */}
        <div className={masterPanelCard}>
          <div className={masterGlowGradientsTop}></div>
          <div className={masterGlowGradientsBottom}></div>
          <div className="relative z-10 grid grid-cols-1 items-center gap-2 lg:grid-cols-2">
            <div className="flex items-start sm:items-center gap-3.5">
              <div className="h-14 w-14 sm:h-16 sm:w-16 overflow-hidden rounded-full border-2 border-slate-200 bg-white flex items-center justify-center shadow-md shrink-0">
                {tenantLogo ? (
                  <img src={tenantLogo} alt="Org Logo" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-xs font-black tracking-wider text-slate-400">LOGO</span>
                )}
              </div>

              <div className="flex flex-col justify-center min-w-0 space-y-0.5">
                <p className={`${masterBadgeControl} w-fit text-[9px] sm:text-[10px] px-2 rounded-md`}>
                  Dashboard
                </p>
                <h1 className={`${masterWelcomeTitle} text-sm sm:text-lg font-extrabold tracking-tight text-slate-900 truncate`}>
                  Welcome, <span className={masterWelcomeSubtitle}>{user?.name || "Student"}</span>
                </h1>
               
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  {selectedExamObjects.length > 0 ? selectedExamObjects.map((item) => {
                    const name = item?.examName || item?.name;
                    const code = item?.examCode;
                    if (!name) return null;
                    return (
                      <span 
                        key={code || name} 
                        className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-white px-3 py-0.5 text-[10px] font-bold text-blue-700 shadow-2xs"
                      >
                        {name}
                        <button
                          type="button"
                          onClick={() => handleRemoveExam(code)}
                          className="text-slate-400 hover:text-rose-600 transition cursor-pointer"
                          title="Remove Exam"
                        >
                          <X size={12} strokeWidth={3} />
                        </button>
                      </span>
                    );
                  }) : (
                    <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[9px] font-bold text-slate-500">
                      No exams assigned for this organization
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={fetchAvailableExams}
                    className="rounded-full border border-blue-300 bg-blue-50 px-2.5 py-0.5 text-[9px] font-bold text-blue-700 hover:bg-blue-100 transition shadow-2xs cursor-pointer"
                  >
                    + Add Exam
                  </button>
                </div>
              </div>
            </div>

            <div className="lg:justify-self-end w-full lg:w-auto">
              <div className="py-0.5 flex items-center justify-between lg:justify-start gap-3">
                <div>
                  <p className="text-[8px] font-bold uppercase tracking-wider text-slate-500">Organization</p>
                  <p className="text-xs font-black tracking-tight text-[#0E3652] truncate max-w-[160px]" title={tenantOrgName}>
                    {tenantOrgName || "Loading..."}
                  </p>
                </div>
                <div className="min-w-[110px] rounded-xl bg-[#0E3652] px-2.5 py-1 text-center text-[#F4B429] shadow-2xs">
                  <p className="text-[8px] font-bold uppercase tracking-wider opacity-90">Latest Result</p>
                  <p className="text-xs font-extrabold tracking-wide mt-0.5">
                    {latestAttempt ? `${formatScore(latestAttempt.obtainedMarks)} / ${formatScore(latestAttempt.totalMarks)}` : "No attempts yet"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Compact Micro Metrics Grid */}
        <div className="grid grid-cols-2 gap-1 shrink-0 md:grid-cols-4">
          {[
            { icon: BookOpenCheck, title: totalAttempts, label: "Total attempts" },
            { icon: BrainCircuit, title: latestAttempt ? latestAttempt.correctAnswers : 0, label: "Latest correct" },
            { icon: ChartNoAxesCombined, title: latestAttempt ? formatScore(latestAttempt.obtainedMarks) : "0.000", label: "Latest score" },
            { icon: Trophy, title: latestAttempt ? getOrdinalSuffix(latestAttempt.attemptNumber) : "NONE", label: "Latest attempt", dynamicSize: true }
          ].map((card, i) => (
            <div key={i} className={masterCardBox}>
              <div className={masterCardIconBox}>
                <card.icon size={14} strokeWidth={2.5} />
              </div>
              <div className={masterCardTextBox}>
                <p className={`${masterCardTitle} ${card.dynamicSize && latestAttempt ? "text-[10px] tracking-wide uppercase" : ""}`}>
                  {card.title}
                </p>
                <p className={masterCardLabel}>{card.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Attempt History Section */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-300/80 bg-white p-2.5 shadow-xs">
          <div className="mb-1.5 flex shrink-0 items-center justify-between border-b border-slate-200 pb-1">
            <div>
              <h2 className={masterHeadingMain}>Attempt History</h2>
            </div>
          </div>

          <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className={masterStickyHeadRow}>
                  <th className="p-1 pl-2.5">Exam</th>
                  <th className="p-1">Topic/Subtopic</th>
                  <th className="p-1 text-center">Attempt</th>
                  <th className="p-1 text-center">Total Qs</th>
                  <th className="p-1 text-center">Attempted</th>
                  <th className="p-1 text-center">Correct</th>
                  <th className="p-1 text-center">Wrong</th>
                  <th className="p-1 text-center">Total Marks</th>
                  <th className="p-1">Score</th>
                  <th className="p-1 text-center">Time Taken</th>
                  <th className="p-1 pr-2.5 text-right">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-transparent">
                {historyRows.length > 0 ? historyRows.map((row, idx) => (
                  <tr 
                    key={`${row.questionSetId}-${row.attemptNumber}-${row.submittedAt}-${idx}`} 
                    className={masterTableRow}
                  >
                    <td className={masterTableCodeCell}>{row.examName || row.examCode || "---"}</td>
                    <td className={masterTableNameCell}>
                        {row.subTopicName || row.topicName || row.subjectName || "---"}
                      </td>
                    <td className="p-1 text-center font-bold text-[#0E3652] text-[11px]">{getOrdinalSuffix(row.attemptNumber || 1)}</td>
                    <td className="p-1 text-center font-bold text-slate-700 text-[11px]">{row.totalQuestions || 0}</td>
                    <td className="p-1 text-center font-bold text-blue-700 text-[11px]">{row.attemptedQuestions || 0}</td>
                    <td className="p-1 text-center font-bold text-emerald-700 text-[11px]">{row.correctAnswers || 0}</td>
                    <td className="p-1 text-center font-bold text-rose-700 text-[11px]">{row.wrongAnswers || 0}</td>
                    <td className="p-1 text-center font-bold text-slate-800 text-[11px]">{formatScore(row.totalMarks)}</td>
                    <td className="p-1 font-bold text-slate-900 text-[11px]">{formatScore(row.obtainedMarks)}</td>
                    <td className="p-1 text-center font-mono text-[10px] font-semibold text-slate-600">{formatDuration(row.timeTakenSeconds)}</td>
                    <td className={masterTableDateCell}>
                      {formatDate(row.submittedAt)}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="11" className={masterEmptyState}>
                      No attempt history found yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* Modal / Popup for Adding Exam */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4 select-none">
          <div className="w-full max-w-sm rounded-2xl bg-white p-4 shadow-xl border border-slate-200">
            <h3 className="text-xs font-bold text-slate-900 mb-2">Add Targeted Exam</h3>
            
            {availableExams.length > 0 ? (
              <div className="space-y-2 mb-3">
                <select
                  value={selectedNewExam}
                  onChange={(e) => setSelectedNewExam(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 p-2 text-xs text-slate-800 outline-none"
                >
                  <option value="">-- Select Exam --</option>
                  {availableExams.map((ex) => (
                    <option key={ex.examCode} value={ex.examCode}>
                      {ex.examName}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <p className="text-[11px] text-slate-500 mb-3">No additional unselected exams available for your organization.</p>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="rounded-xl px-3 py-1.5 text-[11px] font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 cursor-pointer"
              >
                Cancel
              </button>
              {availableExams.length > 0 && (
                <button
                  type="button"
                  onClick={handleAddExamSubmit}
                  disabled={!selectedNewExam || addingLoading}
                  className="rounded-xl px-3 py-1.5 text-[11px] font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 cursor-pointer"
                >
                  {addingLoading ? "Saving..." : "Save Exam"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 3px;
          height: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(14, 54, 82, 0.2);
          border-radius: 99px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(14, 54, 82, 0.4);
        }
      `}</style>
    </div>
  );
};

export default StudentDashboard;